import { useState } from 'react';
import { X, Loader2, Image as ImageIcon } from 'lucide-react';
import type { Card } from '../App';

interface SaveDeckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (name: string, format: string, coverImage: string | null) => Promise<void>;
    deck: Card[];
    defaultName: string;
    defaultFormat: string;
}

const FORMATS = [
    { value: 'standard', label: 'Standard' },
    { value: 'modern', label: 'Modern' },
    { value: 'pioneer', label: 'Pioneer' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'commander', label: 'Commander' },
    { value: 'pauper', label: 'Pauper' },
    { value: 'limited', label: 'Limited' },
    { value: 'historic', label: 'Historic' },
    { value: 'explorer', label: 'Explorer' },
    { value: 'other', label: 'Other' },
];

export function SaveDeckModal({ isOpen, onClose, onSave, deck, defaultName, defaultFormat }: SaveDeckModalProps) {
    const [name, setName] = useState(defaultName || 'Untitled Deck');
    const [format, setFormat] = useState(defaultFormat || 'standard');
    const [selectedCard, setSelectedCard] = useState<Card | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Get non-land cards for cover image selection
    const coverableCards = deck.filter(card => !card.isLand && card.imageUrl);

    // Convert a Scryfall image URL to art_crop format
    const getArtCropUrl = (imageUrl: string): string => {
        if (!imageUrl) return '';
        // Scryfall URLs follow pattern: https://cards.scryfall.io/normal/front/a/b/abc123.jpg
        // Art crop pattern: https://cards.scryfall.io/art_crop/front/a/b/abc123.jpg
        return imageUrl.replace('/normal/', '/art_crop/').replace('/large/', '/art_crop/').replace('/png/', '/art_crop/');
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Please enter a deck name');
            return;
        }

        setSaving(true);
        setError('');

        try {
            // Use art_crop URL for cover image instead of full card
            const coverImage = selectedCard?.imageUrl ? getArtCropUrl(selectedCard.imageUrl) : null;
            await onSave(name, format, coverImage);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save deck');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/40 hover:text-white/60"
                >
                    <X className="size-5" />
                </button>

                <h2 className="text-xl font-bold text-white mb-6">Save Deck to Discover</h2>

                {/* Deck Name */}
                <div className="mb-4">
                    <label className="block text-sm text-white/60 mb-2">Deck Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter deck name..."
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                    />
                </div>

                {/* Format */}
                <div className="mb-4">
                    <label className="block text-sm text-white/60 mb-2">Format</label>
                    <select
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                    >
                        {FORMATS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </select>
                </div>

                {/* Cover Image Selection */}
                <div className="mb-6">
                    <label className="block text-sm text-white/60 mb-2">
                        <ImageIcon className="inline size-4 mr-1" />
                        Cover Image (click to select)
                    </label>
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 bg-white/5 rounded-lg border border-white/10">
                        {coverableCards.length === 0 ? (
                            <p className="col-span-4 text-center text-white/40 text-sm py-4">
                                No cards with images available
                            </p>
                        ) : (
                            coverableCards.slice(0, 20).map(card => (
                                <button
                                    key={card.id}
                                    onClick={() => setSelectedCard(card === selectedCard ? null : card)}
                                    className={`relative aspect-[2.5/3.5] rounded overflow-hidden border-2 transition-all ${selectedCard?.id === card.id
                                        ? 'border-cyan-500 ring-2 ring-cyan-500/50'
                                        : 'border-transparent hover:border-white/30'
                                        }`}
                                >
                                    <img
                                        src={card.imageUrl}
                                        alt={card.name}
                                        className="w-full h-full object-cover"
                                    />
                                    {selectedCard?.id === card.id && (
                                        <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                                            <span className="text-white text-xs font-bold">✓</span>
                                        </div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                    {selectedCard && (
                        <p className="mt-2 text-xs text-cyan-400">
                            Selected: {selectedCard.name}
                        </p>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <p className="text-red-400 text-sm mb-4">{error}</p>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 py-3 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="size-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save to Discover'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
